export type GoogleMapsLinkParseResult =
  | { ok: true; latitude: number; longitude: number }
  | { ok: false; reason: 'empty' | 'short-link' | 'not-found' };

const SHORT_LINK_PATTERN = /^https?:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps)\//i;

// Precise pinned-place coordinates, e.g. ".../data=!4m6!3m5!...!8m2!3d24.7137!4d46.6754!...".
// Preferred over @lat,lng when present - that's just the map viewport center at share time,
// which can drift from the actual dropped pin once the map's been panned or zoomed since.
const PLACE_PIN_PATTERN = /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/;

// Map viewport center, e.g. ".../@24.7136,46.6753,15z".
const VIEWPORT_PATTERN = /@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/;

// "?q=lat,lng" or "&q=lat,lng" query form.
const QUERY_PATTERN = /[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/;

// A bare "lat, lng" pair pasted without a surrounding URL at all.
const BARE_COORDS_PATTERN = /^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/;

function toResult(latText: string, lngText: string): GoogleMapsLinkParseResult {
  const latitude = Number(latText);
  const longitude = Number(lngText);
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180
  ) {
    return { ok: false, reason: 'not-found' };
  }
  return { ok: true, latitude, longitude };
}

/** Extracts a lat/lng pair from a pasted Google Maps link (or a bare "lat, lng" string). */
export function parseGoogleMapsLocation(input: string): GoogleMapsLinkParseResult {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, reason: 'empty' };

  if (SHORT_LINK_PATTERN.test(trimmed)) {
    return { ok: false, reason: 'short-link' };
  }

  const placeMatch = trimmed.match(PLACE_PIN_PATTERN);
  if (placeMatch) return toResult(placeMatch[1], placeMatch[2]);

  const viewportMatch = trimmed.match(VIEWPORT_PATTERN);
  if (viewportMatch) return toResult(viewportMatch[1], viewportMatch[2]);

  const queryMatch = trimmed.match(QUERY_PATTERN);
  if (queryMatch) return toResult(queryMatch[1], queryMatch[2]);

  const bareMatch = trimmed.match(BARE_COORDS_PATTERN);
  if (bareMatch) return toResult(bareMatch[1], bareMatch[2]);

  return { ok: false, reason: 'not-found' };
}
